import { useState, useRef, useEffect } from "react";
import { useT } from "@/i18n";
import { uiMessages } from "@/i18n/messages/ui";
import { cn } from "@/lib/cn";

/**
 * InlineEditText — click-to-edit text field.
 *
 * Default display is a plain span. On click it switches to an auto-focused
 * input. Enter or blur confirms the edit (calls onSave if value changed and
 * non-empty). Escape reverts to the original value.
 */

interface InlineEditTextProps {
  value: string;
  onSave: (newValue: string) => void;
  maxLength?: number;
  /** Start in edit mode immediately (e.g. triggered from a context menu). */
  startEditing?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function InlineEditText({
  value,
  onSave,
  maxLength,
  startEditing: startInEditMode = false,
  className,
  "data-testid": testId = "inline-edit-text",
}: InlineEditTextProps) {
  const t = useT(uiMessages);
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep draft in sync when value prop changes externally
  useEffect(() => {
    if (!isEditing) {
      setDraft(value);
    }
  }, [value, isEditing]);

  function startEditing() {
    setDraft(value);
    setIsEditing(true);
  }

  function commit() {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    }
    setIsEditing(false);
  }

  function cancel() {
    setDraft(value);
    setIsEditing(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  }

  // Auto-focus and select all text when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        maxLength={maxLength}
        className={cn(
          "rounded-control-sm border bg-glass-fill-heavy px-appkit-2xs py-appkit-2xs outline-none",
          "focus:border-accent focus:ring-1",
          "text-primary transition-colors",
          className,
        )}
        data-testid={`${testId}-input`}
        aria-label={t("editText")}
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={startEditing}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          startEditing();
        }
      }}
      className={cn("cursor-pointer text-primary", className)}
      data-testid={testId}
      title={t("clickToEdit")}
    >
      {value}
    </span>
  );
}

export type { InlineEditTextProps };
